// Standalone NLU design worksheet — the deep Lex/NLU design deliverable (the Viamericas-style
// workbook) generated client-side with SheetJS when a Design Model warrants a large NLU bot
// (decideLexStrategy → STANDALONE_NLU). For small bots the Lex config lives in the DDD's Lex tabs and
// this document is unnecessary; for a big multi-intent / multi-language bot the full NLU design needs
// its own working document (bot config, intents × all languages utterances, slots, slot types).
//
// Deterministic from the loaded Design Model — no backend/job (mirrors customerWorksheet.js).

import * as XLSX from 'xlsx';

// Render a bot's confidence threshold(s): the per-locale map (Stage 2.2) if present, else the scalar.
function formatThresholds(bot) {
  const perLocale = bot.confidenceThresholds;
  if (perLocale && typeof perLocale === 'object' && Object.keys(perLocale).length) {
    return Object.entries(perLocale).map(([loc, n]) => `${loc}: ${n}`).join('; ');
  }
  return bot.confidenceThreshold ?? 0.4;
}

function botSummaryAoA(model) {
  const meta = model.meta || {};
  const bots = (model.lexBots || []).length ? model.lexBots : [{ name: `${meta.customer || 'Customer'} Bot` }];
  const intents = model.intents || [];
  // Stage 2.2: session-management + fallback + disambiguation + dialog-hook columns join the summary.
  const header = ['Bot', 'Languages', 'Confidence threshold(s)', 'Dialog hook', 'Fulfillment Lambda',
    'Disambiguation margin', 'Fallback strikes', 'Escalation queue', 'End-conversation intent',
    'Anything-else loop', 'Intents', 'Slot types'];
  const rows = bots.map((bot) => {
    const refs = Array.isArray(bot.intentRefs) && bot.intentRefs.length ? new Set(bot.intentRefs) : null;
    const count = refs ? intents.filter((i) => refs.has(i.name) || refs.has(i.id)).length : intents.length;
    const sm = bot.sessionManagement || {};
    return [
      bot.name || '',
      (meta.languages || ['en']).join(', '),
      formatThresholds(bot),
      bot.dialogHookRef || '',
      bot.fulfillmentLambdaRef || '',
      bot.disambiguation?.margin ?? '',
      bot.fallback?.maxStrikes ?? '',
      bot.fallback?.escalationQueue || bot.sessionManagement?.escalationQueue || '',
      sm.endConversationIntent ? 'Yes' : '',
      sm.anythingElseLoop ? 'Yes' : '',
      count,
      (model.slotTypes || []).length,
    ];
  });
  return [header, ...rows];
}

// Stage 2.6: Session-attribute contract rows — the pinned attribute names each side reads/writes.
function sessionAttributesAoA(model) {
  const header = ['Session attribute'];
  const attrs = Array.isArray(model.meta?.sessionAttributeContract) ? model.meta.sessionAttributeContract : [];
  const rows = attrs.map((a) => [typeof a === 'string' ? a : (a?.name || '')]).filter((r) => r[0]);
  if (rows.length === 0) rows.push(['(No session-attribute contract defined.)']);
  return [header, ...rows];
}

// Stage 2.6: Slot Elicitation Config — mirrors the Viamericas 7-Slot Elicitation Config sheet.
function elicitationConfigAoA(model) {
  const header = ['Intent', 'Slot', 'Slot type', 'Mode', 'DTMF entry', 'Confirm read-back',
    'Base prompt', 'Help prompt', 'Give-up prompt', 'Give-up queue', 'Runtime hints'];
  const rows = [];
  for (const intent of model.intents || []) {
    for (const slot of intent.slots || []) {
      const p = slot.prompts || {};
      const mode = slot.elicitationMode || (slot.required === false ? 'optional' : 'required_always');
      rows.push([
        intent.name || '',
        slot.name || '',
        slot.slotType || slot.type || '',
        mode,
        slot.dtmfEntry ? 'Yes' : '',
        slot.confirmationReadback ? 'Yes' : '',
        p.base || slot.prompt || '',
        p.help || '',
        p.giveUp || '',
        slot.giveUpQueue || intent.defaultQueue || '',
        Array.isArray(slot.runtimeHints) ? slot.runtimeHints.join(', ') : '',
      ]);
    }
  }
  if (rows.length === 0) rows.push(['(No slots with elicitation config — single-shot intents.)', '', '', '', '', '', '', '', '', '', '']);
  return [header, ...rows];
}

// Stage 2.6: Personas / intent permissions.
function personasAoA(model) {
  const header = ['Persona', 'Description', 'Self-service intents', 'Route-only intents'];
  const rows = (model.personas || []).map((p) => [
    p.name || '',
    p.description || '',
    (p.intentPermissions?.selfService || []).join(', '),
    (p.intentPermissions?.routeOnly || []).join(', '),
  ]);
  if (rows.length === 0) rows.push(['(No personas defined.)', '', '', '']);
  return [header, ...rows];
}

function intentsAoA(model) {
  const header = ['Intent', 'Description', 'Self-service?', 'Default queue', 'Citation status', 'Confidence'];
  const rows = (model.intents || []).map((i) => [
    i.name || '',
    i.description || '',
    i.selfService ? 'Yes' : 'No',
    i.defaultQueue || '',
    i.citationStatus || '',
    i.confidence || '',
  ]);
  if (rows.length === 0) rows.push(['(No intents defined yet.)', '', '', '', '', '']);
  return [header, ...rows];
}

function utterancesAoA(model) {
  const header = ['Intent', 'Language', 'Sample utterance'];
  const rows = [];
  for (const intent of model.intents || []) {
    const u = intent.utterances || {};
    for (const [lang, arr] of Object.entries(u)) {
      for (const utt of Array.isArray(arr) ? arr : []) rows.push([intent.name || '', lang, utt]);
    }
  }
  if (rows.length === 0) rows.push(['(No utterances yet — add ≥8–10 per intent.)', '', '']);
  return [header, ...rows];
}

function slotsAoA(model) {
  const header = ['Intent', 'Slot', 'Slot type', 'Required?', 'Elicitation prompt'];
  const rows = [];
  for (const intent of model.intents || []) {
    for (const slot of intent.slots || []) {
      rows.push([
        intent.name || '',
        slot.name || '',
        slot.slotType || slot.type || '',
        slot.required ? 'Yes' : 'No',
        slot.prompt || '',
      ]);
    }
  }
  if (rows.length === 0) rows.push(['(No slots — single-shot intents.)', '', '', '', '']);
  return [header, ...rows];
}

// Canonical slotType synonym shape is { lang: [...] } (per-value, matches build/lexProjection.js and
// design-model/verify.js). Tolerant of the legacy flat-array shape too (C.7 — this used to disagree
// with lexProjection.js, which read a { lang: [...] } object here).
function formatSynonyms(raw) {
  if (Array.isArray(raw)) return raw.join(', ');
  if (raw && typeof raw === 'object') {
    return Object.entries(raw)
      .filter(([, arr]) => Array.isArray(arr) && arr.length)
      .map(([lang, arr]) => `${lang}: ${arr.join(', ')}`)
      .join('; ');
  }
  return '';
}

function slotTypesAoA(model) {
  const header = ['Slot type', 'Value', 'Synonyms'];
  const rows = [];
  for (const st of model.slotTypes || []) {
    const values = st.values || [];
    if (values.length === 0) {
      rows.push([st.name || '', '', '']);
      continue;
    }
    for (const v of values) {
      const value = typeof v === 'string' ? v : v.value || '';
      const synonyms = formatSynonyms(v?.synonyms) || formatSynonyms(st.synonyms?.[value]);
      rows.push([st.name || '', value, synonyms]);
    }
  }
  if (rows.length === 0) rows.push(['(No custom slot types — built-in types only.)', '', '']);
  return [header, ...rows];
}

function routingAoA(model) {
  const header = ['Intent', 'Condition', 'Target queue', 'Mechanism'];
  const rows = (model.routingMatrix || []).map((r) => [r.intent || '', r.condition || '', r.targetQueue || '', r.mechanism || '']);
  if (rows.length === 0) rows.push(['(No routing matrix yet.)', '', '', '']);
  return [header, ...rows];
}

function buildNluWorkbook(model) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(botSummaryAoA(model)), 'Bot Summary');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(intentsAoA(model)), 'Intents');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(utterancesAoA(model)), 'Utterances');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(slotsAoA(model)), 'Slots');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(elicitationConfigAoA(model)), 'Slot Elicitation Config');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(slotTypesAoA(model)), 'Slot Types');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(personasAoA(model)), 'Personas');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sessionAttributesAoA(model)), 'Session Attributes');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(routingAoA(model)), 'Routing');
  return wb;
}

// Exposed for the shared-shape tolerance test (nluWorksheet.test.js) — not part of the public API.
export const __internal = { slotTypesAoA, formatSynonyms, elicitationConfigAoA, personasAoA, sessionAttributesAoA, botSummaryAoA };

/** Build the NLU worksheet into an ArrayBuffer (no download) — for persisting as an artifact. */
export function buildNluArrayBuffer(designModel) {
  return XLSX.write(buildNluWorkbook(designModel || {}), { type: 'array', bookType: 'xlsx' });
}

/** Build + download the standalone NLU design worksheet for a Design Model. */
export function buildNluWorksheet(designModel, { projectName, filename } = {}) {
  const model = designModel || {};
  const safeName = (projectName || model.meta?.customer || 'Project').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
  XLSX.writeFile(buildNluWorkbook(model), filename || `${safeName}_NLU_Design.xlsx`);
}

export default buildNluWorksheet;
